import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { FEATURE_CATEGORIES, PRICING_TIERS } from './constants';
import { FeatureItem } from './types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Accent colors for each tier column
const tierAccentClasses: Record<string, string> = {
  growth: 'text-grow',
  management: 'text-primary',
  complete: 'text-secondary',
  enterprise: 'bg-gradient-to-r from-grow to-primary bg-clip-text text-transparent',
};

const FeatureCell: React.FC<{
  value: boolean | string;
  tier: string;
}> = ({ value, tier }) => {
  if (value === true) {
    return (
      <div className="flex justify-center">
        <Check className={cn(
          'w-5 h-5',
          tier === 'growth' && 'text-grow',
          tier === 'management' && 'text-primary',
          tier === 'complete' && 'text-secondary',
          tier === 'enterprise' && 'text-grow',
        )} />
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex justify-center">
        <X className="w-5 h-5 text-foreground" />
      </div>
    );
  }
  return (
    <div className="flex justify-center">
      <span className="text-xs font-medium text-foreground">
        {value}
      </span>
    </div>
  );
};

export const FeatureComparisonTable: React.FC = () => {
  // Mobile plan selector - default to 'complete' (most popular)
  const [selectedPlan, setSelectedPlan] = useState('complete');

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-muted/30 via-background to-muted/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Compare All Features
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            See exactly what's included in each plan.
          </p>
        </motion.div>

        {/* Desktop: Full Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="hidden lg:block"
        >
          <div className="bg-card rounded-xl border border-border">
            <table className="w-full">
              {/* Sticky Header with Plan Names and Prices */}
              <thead className="sticky top-12 z-10 bg-card border-b border-border shadow-sm [&>tr>th:first-child]:rounded-tl-xl [&>tr>th:last-child]:rounded-tr-xl">
                <tr>
                  <th className="text-left px-6 py-4 w-[280px]">
                    <span className="text-sm font-medium text-muted-foreground">Feature</span>
                  </th>
                  {PRICING_TIERS.map((tier) => (
                    <th key={tier.id} className="text-center px-4 py-4">
                      <div className={cn('font-semibold text-base', tierAccentClasses[tier.id])}>
                        {tier.name}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {tier.monthlyPrice ? `€${tier.monthlyPrice}/mo` : 'Custom'}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_CATEGORIES.map((category) => {
                  const Icon = category.icon;
                  return (
                    <>
                      {/* Category Header Row */}
                      <tr key={`cat-${category.id}`} className="border-t border-border">
                        <td colSpan={5} className="px-6 py-3 bg-card">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Icon className="w-3.5 h-3.5 text-primary" />
                            </div>
                            <span className="font-semibold text-foreground text-sm">
                              {category.name}
                            </span>
                          </div>
                        </td>
                      </tr>
                      {/* Feature Rows */}
                      {category.features.map((feature) => (
                        <tr
                          key={`${category.id}-${feature.name}`}
                          className="border-b border-border/50 last:border-0"
                        >
                          <td className="px-6 py-3 text-sm text-foreground">
                            {feature.name}
                          </td>
                          <td className="px-4 py-3">
                            <FeatureCell value={feature.growth} tier="growth" />
                          </td>
                          <td className="px-4 py-3">
                            <FeatureCell value={feature.management} tier="management" />
                          </td>
                          <td className="px-4 py-3">
                            <FeatureCell value={feature.complete} tier="complete" />
                          </td>
                          <td className="px-4 py-3">
                            <FeatureCell value={feature.enterprise} tier="enterprise" />
                          </td>
                        </tr>
                      ))}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Mobile: Plan Selector + Single Plan View */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:hidden"
        >
          {/* Plan Selector */}
          <div className="sticky top-12 z-10 bg-background py-4 -mx-4 px-4 sm:-mx-6 sm:px-6">
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Select a plan to view features
            </label>
            <Select value={selectedPlan} onValueChange={setSelectedPlan}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRICING_TIERS.map((tier) => (
                  <SelectItem key={tier.id} value={tier.id}>
                    <span className={tierAccentClasses[tier.id]}>{tier.name}</span>
                    <span className="text-muted-foreground ml-2">
                      {tier.monthlyPrice ? `€${tier.monthlyPrice}/mo` : 'Custom'}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selected Plan Features */}
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            {FEATURE_CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <div key={category.id}>
                  {/* Category Header */}
                  <div className="border-t border-border px-4 py-3 flex items-center gap-2 first:border-t-0">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="font-semibold text-foreground text-sm">
                      {category.name}
                    </span>
                  </div>
                  {/* Feature List */}
                  <div className="divide-y divide-border/50">
                    {category.features.map((feature) => {
                      const value = feature[selectedPlan as keyof FeatureItem] as boolean | string;
                      return (
                        <div
                          key={feature.name}
                          className="flex items-center justify-between px-4 py-3"
                        >
                          <span className="text-sm text-foreground">{feature.name}</span>
                          <FeatureCell value={value} tier={selectedPlan} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeatureComparisonTable;
